/**
 * @fileoverview Own the unified staff workspace connecting employee identity, attorney roles/rates, integrations, and phone synchronization. The lifecycle API owns saved state; edits and provider refreshes are explicit actions. `EmployeeAccess.js` separately owns workspace choices and `PhoneAdmin.js` owns global phone settings. Filtering or editing displayed people does not authorize unrelated resource access or replace backend lifecycle checks.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import Avatar from '../Avatar/Avatar';
import ToastContext from '../../ToastContext';
import api from '../../api';
import { AppButton, AppMenu, AppModal, AppTextInput, AppToggle } from '../shared';
import { AdminCard, AdminEmptyState, AdminPage, AdminSection } from './AdminChrome';
import './People.css';
import AppIcon from "../shared/AppIcon";

const DEFAULT_STATUS_FILTER = ['active'];



function createDefaultFilters() {
  return {
    search: '',
    status: [...DEFAULT_STATUS_FILTER],
    role: [],
    billable: [],
    tester: []
  };
}

const DEFAULT_FORM = {
  employeeId: null,
  Name: '',
  Email: '',
  ProfilePic: null,
  IsActive: true,
  IsAdmin: false,
  IsCurrentUser: false,
  PipedriveUserID: '',
  ClockifyUserID: '',
  TrelloMemberId: '',
  SlackUserId: '',
  IsDesignatedSlackTester: false,
  IsAttorney: false,
  IsAvailable: true,
  HasBillableRate: false,
  HourlyRate: '',
  Extension: '',
  OriginalExtension: '',
  DirectLines: [],
  PhoneSyncState: 'not_configured',
  PhoneSyncError: null
};

const FILTER_GROUPS = [
{
  key: 'status',
  label: 'Status',
  icon: 'pi pi-flag',
  options: [
  { value: 'active', label: 'Active' },
  { value: 'departed', label: 'Departed' }]

},
{
  key: 'role',
  label: 'Role',
  icon: 'pi pi-briefcase',
  options: [
  { value: 'attorney', label: 'Attorney' },
  { value: 'non_attorney', label: 'Non-Attorney' }]

},
{
  key: 'billable',
  label: 'Billable Rate',
  icon: 'pi pi-dollar',
  options: [
  { value: 'has_rate', label: 'Has Rate' },
  { value: 'no_rate', label: 'No Rate' }]

},
{
  key: 'tester',
  label: 'Slack Tester',
  icon: 'pi pi-send',
  options: [
  { value: 'tester', label: 'Tester' },
  { value: 'standard', label: 'Standard' }]

}];




function People({ embedded = false }) {
  const showToast = useContext(ToastContext);
  const [rows, setRows] = useState([]);
  const [phoneInventory, setPhoneInventory] = useState({ directLines: [], reservedExtensions: [], extensionRange: { minimum: 100, maximum: 199 } });
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState(createDefaultFilters);
  const [modalState, setModalState] = useState({
    visible: false,
    mode: 'add',
    form: DEFAULT_FORM,
    error: ''
  });

  const applyPayload = useCallback(

  (payload) => {
    setRows(Array.isArray(payload?.rows) ? payload.rows : []);
    setPhoneInventory(payload?.phoneInventory || { directLines: [], reservedExtensions: [], extensionRange: { minimum: 100, maximum: 199 } });
  }, []);

  const fetchRoster = useCallback(

  async (showRefreshedToast = false) => {
    setLoading(true);
    try {
      const response = await api.get('/employee_lifecycle');
      applyPayload(response.data || {});
      if (showRefreshedToast) {
        showToast({ severity: 'info', summary: 'Successful', detail: 'People refreshed' });
      }
    } catch (error) {
      console.error(error);
      if (showRefreshedToast) {
        showToast({ severity: 'error', summary: 'Error', detail: 'People not refreshed' });
      }
    } finally {
      setLoading(false);
    }
  }, [applyPayload, showToast]);

  useEffect(() => {
    fetchRoster(false);
  }, [fetchRoster]);

  const filteredRows = useMemo(
    () => rows.filter((person) => matchesFilters(person, filters)),
    [filters, rows]
  );

  const openAddModal = useCallback(

  () => {
    setModalState({
      visible: true,
      mode: 'add',
      form: DEFAULT_FORM,
      error: ''
    });
  }, []);

  const openEditModal = useCallback(

  (person) => {
    setModalState({
      visible: true,
      mode: 'edit',
      form: buildPersonForm(person),
      error: ''
    });
  }, []);

  const closeModal = useCallback(

  () => {
    if (saving) {
      return;
    }

    setModalState({
      visible: false,
      mode: 'add',
      form: DEFAULT_FORM,
      error: ''
    });
  }, [saving]);

  const updateModalField = useCallback(

  (field, value) => {
    setModalState((previous) => {
      const nextForm = {
        ...previous.form,
        [field]: value
      };

      if (field === 'IsAttorney' && value) {
        nextForm.HasBillableRate = true;
      }

      return {
        ...previous,
        error: '',
        form: nextForm
      };
    });
  }, []);

  const savePerson = useCallback(

  async () => {
    const validationError = validatePersonForm(modalState.form);
    if (validationError) {
      setModalState((previous) => ({ ...previous, error: validationError }));
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/employee_lifecycle/onboard', buildSavePayload(modalState.form));
      applyPayload(response.data || {});
      showToast({
        severity: 'success',
        summary: 'Successful',
        detail: modalState.mode === 'add' ? 'Person added' : 'Person updated'
      });
      setModalState({
        visible: false,
        mode: 'add',
        form: DEFAULT_FORM,
        error: ''
      });
    } catch (error) {
      console.error(error);
      setModalState((previous) => ({
        ...previous,
        error: error?.response?.data?.error || 'Person could not be saved'
      }));
    } finally {
      setSaving(false);
    }
  }, [applyPayload, modalState.form, modalState.mode, showToast]);

  const offboardPerson = useCallback(

  async (person) => {
    const name = person?.Name || 'this person';
    const confirmed = window.confirm(
      `Move ${name} to departed people? This will mark them inactive for automations and Slack delivery.`
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await api.post(`/employee_lifecycle/${person.employeeId}/offboard`);
      applyPayload(response.data || {});
      showToast({ severity: 'success', summary: 'Successful', detail: `${name} moved to departed` });
    } catch (error) {
      console.error(error);
      showToast({
        severity: 'error',
        summary: 'Error',
        detail: error?.response?.data?.error || `${name} was not moved to departed`
      });
    }
  }, [applyPayload, showToast]);

  const deletePerson = useCallback(async (person) => {
    if (!window.confirm(`Delete ${person.Name}'s profile? Source sync will not recreate it. Historical records will be retained.`)) return;
    try {
      const response = await api.delete(`/employee_lifecycle/${person.employeeId}`);
      applyPayload(response.data || {});
      showToast({ severity: 'success', summary: 'Successful', detail: 'Profile deleted' });
    } catch (error) {
      showToast({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Profile could not be deleted' });
    }
  }, [applyPayload, showToast]);

  const restorePerson = useCallback(async (person) => {
    try {
      const response = await api.post('/employee_lifecycle/onboard', {
        ...buildSavePayload(buildPersonForm(person)),
        IsActive: true
      });
      applyPayload(response.data || {});
      showToast({ severity: 'success', summary: 'Successful', detail: `${person.Name || 'Person'} restored to active` });
    } catch (error) {
      console.error(error);
      showToast({
        severity: 'error',
        summary: 'Error',
        detail: error?.response?.data?.error || `${person.Name || 'Person'} was not restored`
      });
    }
  }, [applyPayload, showToast]);

  const handleStatusAction = useCallback(

  (person) => {
    if (person.IsActive === false) {
      restorePerson(person);
      return;
    }

    offboardPerson(person);
  }, [offboardPerson, restorePerson]);

  const syncSources = useCallback(

  async () => {
    setSyncing(true);
    try {
      await api.post('/employees/refresh');
      showToast({ severity: 'success', summary: 'Successful', detail: 'People synced from source systems' });
      await fetchRoster(false);
    } catch (error) {
      console.error(error);
      showToast({ severity: 'error', summary: 'Error', detail: 'People were not synced from source systems' });
    } finally {
      setSyncing(false);
    }
  }, [fetchRoster, showToast]);

  const handleFilterChange = useCallback(

  (field, value) => {
    setFilters((previous) => ({
      ...previous,
      [field]: value
    }));
  }, []);

  const handleFilterRemove = useCallback(

  (field) => {
    setFilters((previous) => ({
      ...previous,
      [field]: field === 'search' ? '' : getDefaultFilterValue(field)
    }));
  }, []);

  const clearAllFilters = useCallback(

  () => {
    setFilters(createDefaultFilters());
  }, []);

  const Wrapper = embedded ? AdminSection : AdminPage;
  const noResults = filteredRows.length === 0;

  return (
    <div className="people-workspace">
            <Wrapper
        title="People"
        description="Manage employee identity mappings, attorney settings, and billable rates from one admin surface while keeping the backend tables separate."
        actions={
        <>
                        <AppButton icon="pi pi-plus" variant="secondary" onClick={openAddModal} label="Add Person" />
                        <AppButton icon="pi pi-refresh" variant="info" onClick={() => fetchRoster(true)} disabled={loading} label="Refresh" />
                        <AppButton
            icon={syncing ? 'pi pi-spin pi-spinner' : 'pi pi-cloud-download'}
            variant="secondary"
            onClick={syncSources}
            disabled={syncing}
            label={syncing ? 'Syncing...' : 'Sync Sources'} />

                    </>}>


                <div className="admin-people-page">
                    <PeopleFilters
            filters={filters}
            filteredCount={filteredRows.length}
            totalCount={rows.length}
            isLoading={loading}
            onFilterChange={handleFilterChange}
            onFilterRemove={handleFilterRemove}
            onClearAll={clearAllFilters} />


                    {noResults ?
          <AdminCard title="People Directory" description="The unified people roster is filtered to this result set.">
                            <AdminEmptyState
              title={rows.length ? 'No people match the current filters' : 'No people found'}
              description={rows.length ? 'Adjust the filters or include departed people to see more results.' : 'Add a person or sync source systems to populate the roster.'} />

                        </AdminCard> :

          <PeopleSection
            title={`People (${filteredRows.length})`}
            description="Active people are shown by default. Use the filters to include departed records when you need them."
            people={filteredRows}
            emptyTitle="No people in this view"
            emptyDescription="Adjust the current filters or add a new person."
            onEdit={openEditModal}
            onStatusAction={handleStatusAction} onDelete={deletePerson} />}


                </div>

                <PeopleEditModal
          visible={modalState.visible}
          mode={modalState.mode}
          person={modalState.form}
          error={modalState.error}
          saving={saving}
          phoneInventory={phoneInventory}
          onClose={closeModal}
          onFieldChange={updateModalField}
          onSave={savePerson} />

            </Wrapper>
        </div>);

}



function PeopleFilters({
  filters,
  filteredCount,
  totalCount,
  isLoading,
  onFilterChange,
  onFilterRemove,
  onClearAll
}) {
  const [isExpanded, setIsExpanded] = useState(true);

  const activeFilters = useMemo(

  () => {
    const nextFilters = [];

    if (filters.search.trim()) {
      nextFilters.push({
        key: 'search',
        label: `Search: "${filters.search.trim()}"`
      });
    }

    FILTER_GROUPS.forEach((group) => {
      if (isDefaultFilterValue(group.key, filters[group.key])) {
        return;
      }

      const selectedLabels = group.options
      .filter((option) => filters[group.key].includes(option.value))
      .map((option) => option.label)
      .join(', ');

      nextFilters.push({
        key: group.key,
        label: `${group.label}: ${selectedLabels}`
      });
    });

    return nextFilters;
  }, [filters]);

  return (
    <div className="filters-wrapper">
            <div className="filters-header">
                <div className="filters-title-section">
                    <AppIcon icon="pi pi-filter filters-icon" />
                    <h2 className="filters-title">Filters</h2>
                    {activeFilters.length > 0 ?
          <span className="filter-count-badge">{activeFilters.length}</span> :
          null}
                </div>

                <div className="filters-actions">
                    <span className="filter-results-count">
                        Showing {filteredCount} of {totalCount} people
                    </span>

                    {activeFilters.length > 0 ?
          <button type="button" className="clear-filters-btn-header" onClick={onClearAll}>
                            <AppIcon icon="pi pi-times" />
                            <span>Clear All</span>
                        </button> :
          null}

                    <button
            type="button"
            className="filters-toggle-btn"
            onClick={() => setIsExpanded((previous) => !previous)}
            aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}>

                        <AppIcon icon={isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} />
                    </button>
                </div>
            </div>

            {isExpanded ?
      <div className="filters-content">
                    {activeFilters.length > 0 ?
        <div className="active-filters-chips" role="region" aria-label="Active filters">
                            {activeFilters.map((filter) =>
          <span key={filter.key} className="filter-chip">
                                    <span>{filter.label}</span>
                                    <button
              type="button"
              aria-label={`Remove ${filter.label} filter`}
              onClick={() => onFilterRemove(filter.key)}>

                                        <AppIcon icon="pi pi-times-circle" />
                                    </button>
                                </span>
          )}
                        </div> :
        null}

                    <div className="filters-grid">
                        <div className="filter-group" data-filter-active={Boolean(filters.search.trim())}>
                            <label htmlFor="people-search-filter" className="filter-label">
                                <AppIcon icon="pi pi-search filter-label-icon" />
                                Search
                            </label>
                            <AppTextInput
              id="people-search-filter"
              value={filters.search}
              onChange={(event) => onFilterChange('search', event.target?.value ?? '')}
              placeholder="Name, email, Slack, Clockify..."
              disabled={isLoading}
              style={{ backgroundColor: 'var(--kc-surface-control)', borderColor: 'var(--kc-surface-edge)' }}
              className={filters.search.trim() ? 'filter-active' : ''} />

                        </div>

                        {FILTER_GROUPS.map((group) =>
          <FilterButtonGroup
            key={group.key}
            filterKey={group.key}
            icon={group.icon}
            label={group.label}
            options={group.options}
            selectedValues={filters[group.key]}
            onChange={onFilterChange} />

          )}
                    </div>
                </div> :
      null}
        </div>);

}



function FilterButtonGroup({ filterKey, icon, label, options, selectedValues, onChange }) {
  return (
    <div className="filter-group" data-filter-active={selectedValues.length > 0}>
            <div className="filter-label">
                <AppIcon icon={`${icon} filter-label-icon`} />
                {label}
            </div>
            <div className="people-filter-options">
                {options.map((option) => {
          const isActive = selectedValues.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              className={`people-filter-option${isActive ? ' is-active' : ''}`}
              onClick={() => onChange(filterKey, toggleFilterValue(selectedValues, option.value))}>

                            {option.label}
                        </button>);

        })}
            </div>
        </div>);

}



function PeopleSection({
  title,
  description,
  people,
  emptyTitle,
  emptyDescription,
  onEdit,
  onStatusAction,
  onDelete
}) {
  return (
    <AdminCard title={title} description={description} padded={false}>
            <div className="people-section__body">
                {people.length > 0 ?
        <div className="people-card-grid" aria-label={title}>
                        {people.map((person) =>
          <PeopleCard
            key={person.employeeId}
            person={person}
            onEdit={onEdit}
            onStatusAction={onStatusAction} onDelete={onDelete} />

          )}
                    </div> :

        <div className="people-section__empty">
                        <AdminEmptyState title={emptyTitle} description={emptyDescription} />
                    </div>}

            </div>
        </AdminCard>);

}

function PeopleCard({ person, onEdit, onStatusAction, onDelete }) {
  const isActive = person.IsActive !== false;
  const name = person.Name || 'Unnamed person';
  const actionMenu = useRef(null);
  const roleValue = person.IsAttorney ? 'Attorney' : 'Employee';
  const availabilityValue = person.IsAttorney ?
  person.IsAvailable ? 'Available' : 'Unavailable' :
  null;
  const billableValue = person.HasBillableRate ?
  person.HourlyRate ? formatRate(person.HourlyRate) : 'Rate required' :
  null;
  const actionLabel = isActive ? 'Move to Departed' : 'Restore to Active';
  const actionMenuItems = useMemo(() => [
  {
    label: actionLabel,
    icon: isActive ? 'pi pi-user-minus' : 'pi pi-replay',
    severity: isActive ? 'danger' : 'success',
    command: () => onStatusAction(person)
  }, ...(!isActive && !person.IsAdmin ? [{
    label: 'Delete Profile', icon: 'pi pi-trash', severity: 'danger',
    command: () => onDelete(person)
  }] : [])],
  [actionLabel, isActive, onStatusAction, onDelete, person]);
  const openEditor = useCallback(() => {
    onEdit(person);
  }, [onEdit, person]);
  const handleCardKeyDown = useCallback(

  (event) => {
    if (event.currentTarget !== event.target) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      openEditor();
    }
  }, [openEditor]);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open details for ${name}`}
      className="people-card"
      onClick={openEditor}
      onKeyDown={handleCardKeyDown}>

            <div className="people-card__header">
                <div className="people-card__identity">
                    <Avatar src={person.ProfilePic} name={name} size={56} />
                    <div style={{ minWidth: 0 }}>
                        <div className="people-card__name">{name}</div>
                        <div className="people-card__email">{detailValue(person.Email)}</div>
                    </div>
                </div>

                <div className="people-card__header-actions">
                    <div className={`people-card__status ${isActive ? 'is-active' : 'is-departed'}`}>
                        {isActive ? 'Active' : 'Departed'}
                    </div>
                    <AppMenu model={actionMenuItems} ref={actionMenu} />
                    <button
            type="button"
            className="people-card__menu-trigger"
            aria-label={`More actions for ${name}`}
            onClick={(event) => {
              event.stopPropagation();
              actionMenu.current.toggle(event);
            }}>

                        <AppIcon icon="pi pi-ellipsis-v" aria-hidden="true" />
                    </button>
                </div>
            </div>

            <div className="people-card__facts" aria-label={`Key details for ${name}`}>
                <FactPill label="Role" value={roleValue} />
                {availabilityValue ?
        <FactPill
          label="Availability"
          value={availabilityValue}
          tone={person.IsAvailable ? 'success' : 'warning'} /> :

        null}
                {billableValue ?
        <FactPill
          label="Rate"
          value={billableValue}
          tone={person.HasBillableRate && !person.HourlyRate ? 'warning' : 'default'} /> :

        null}
                {person.IsDesignatedSlackTester ?
        <FactPill label="Slack Tester" value="Enabled" tone="info" /> :
        null}
                {person.IsAdmin ?
        <FactPill label="Admin" value={person.IsCurrentUser ? 'You' : 'Enabled'} tone="info" /> :
        null}
                {person.Extension ?
        <FactPill label="Phone" value={phoneSyncLabel(person.PhoneSyncState)} tone={phoneSyncTone(person.PhoneSyncState)} /> :
        null}
            </div>

            <div className="people-card__systems" aria-label={`System mappings for ${name}`}>
                <div className="people-card__section-label">Systems</div>
                <div className="people-card__system-list">
                    <SystemRow label="Slack" value={detailValue(person.SlackUserId)} muted={!person.SlackUserId} />
                    <SystemRow label="Pipedrive" value={detailValue(person.PipedriveUserID)} muted={!person.PipedriveUserID} />
                    <SystemRow label="Clockify" value={detailValue(person.ClockifyUserID)} muted={!person.ClockifyUserID} />
                    <SystemRow label="Trello" value={detailValue(person.TrelloMemberId)} muted={!person.TrelloMemberId} />
                    <SystemRow label="Extension" value={person.Extension ? `Ext. ${person.Extension}` : 'Not assigned'} muted={!person.Extension} />
                    <SystemRow label="Direct line" value={(person.DirectLines || []).join(', ') || 'Not assigned'} muted={!person.DirectLines?.length} />
                </div>
            </div>
        </div>);

}



function FactPill({ label, value, muted = false, tone = 'default' }) {
  return (
    <div className={`people-fact-pill${muted ? ' is-muted' : ''}${tone === 'default' ? '' : ` is-${tone}`}`}>
            <span className="people-fact-pill__label">{label}</span>
            <span className="people-fact-pill__value">{value}</span>
        </div>);

}



function SystemRow({ label, value, muted = false }) {
  return (
    <div className={`people-system-row${muted ? ' is-muted' : ''}`}>
            <div className="people-system-row__label">{label}</div>
            <div className="people-system-row__value" title={value}>{value}</div>
        </div>);

}



function PhoneSyncBadge({ state, error }) {
  const label = phoneSyncLabel(state);
  return (
    <span className={`people-phone-sync is-${phoneSyncTone(state)}`} title={error || label}>
      <AppIcon icon={state === 'error' ? 'pi pi-exclamation-circle' : state === 'ok' ? 'pi pi-check-circle' : 'pi pi-clock'} />
      {label}
    </span>
  );
}



function phoneSyncLabel(state) {
  if (state === 'ok') return 'Synced';
  if (state === 'error') return 'Error';
  if (state === 'pending') return 'Pending';
  return 'Not configured';
}



function phoneSyncTone(state) {
  if (state === 'ok') return 'success';
  if (state === 'error') return 'warning';
  return state === 'pending' ? 'info' : 'default';
}



function PeopleEditModal({
  visible,
  mode,
  person,
  error,
  saving,
  phoneInventory,
  onClose,
  onFieldChange,
  onSave
}) {
  if (!person) {
    return null;
  }

  const name = person.Name || 'New person';
  const isBillable = person.IsAttorney || person.HasBillableRate;
  const personEmail = String(person.Email || '').trim().toLowerCase();
  const directLineOptions = (phoneInventory?.directLines || []).map(

  (line) => {
    const assignedEmail = String(line.assignedEmail || '').trim();
    const ownedByAnother = Boolean(assignedEmail) && assignedEmail.toLowerCase() !== personEmail;
    const detail = ownedByAnother ? `Assigned to ${assignedEmail}` : line.displayName || 'Available';
    return {
      value: line.did,
      label: `${line.did} — ${detail}`,
      disabled: ownedByAnother
    };
  });

  return (
    <AppModal
      visible={visible}
      title={mode === 'add' ? 'Add Person' : `Edit ${name}`}
      description="Maintain employee identity mappings, attorney settings, and billable rate details from one modal."
      onClose={onClose}
      width={820}
      className="people-edit-modal"
      footer={
      <>
                    <AppButton variant="secondary" onClick={onClose} label="Cancel" disabled={saving} />
                    <AppButton variant="success" onClick={onSave} label={saving ? 'Saving...' : 'Save'} disabled={saving} />
                </>}>


            <div className="people-modal__hero">
                <Avatar src={person.ProfilePic} name={name} size={64} />
                <div>
                    <div className="people-modal__eyebrow">{person.IsActive === false ? 'Departed Profile' : 'Active Profile'}</div>
                    <div className="people-modal__title">{name}</div>
                    <div className="people-modal__subtitle">
                        {person.IsAttorney ? 'Attorney settings enabled.' : 'Employee-only settings enabled.'}
                    </div>
                </div>
            </div>

            <div className="people-modal__grid">
                <ModalInputField person={person} field="Name" label="Name" value={person.Name} onFieldChange={onFieldChange} />
                <ModalInputField person={person} field="Email" label="Email" value={person.Email} onFieldChange={onFieldChange} type="email" />
                <ModalInputField person={person} field="PipedriveUserID" label="Pipedrive User ID" value={person.PipedriveUserID} onFieldChange={onFieldChange} type="number" />
                <ModalInputField person={person} field="ClockifyUserID" label="Clockify User ID" value={person.ClockifyUserID} onFieldChange={onFieldChange} />
                <ModalInputField person={person} field="TrelloMemberId" label="Trello Member ID" value={person.TrelloMemberId} onFieldChange={onFieldChange} />
                <ModalInputField person={person} field="SlackUserId" label="Slack User ID" value={person.SlackUserId} onFieldChange={onFieldChange} />
            </div>

            <div className="people-modal__panel">
                <div className="people-modal__panel-header">
                    <div>
                        <div className="people-modal__panel-title">Attorney Role</div>
                        <div className="people-modal__panel-description">
                            Enable this if the person should also exist in the attorney directory for routing and availability.
                        </div>
                    </div>
                    <AppToggle
            checked={person.IsAttorney}
            ariaLabel="Attorney Role"
            onChange={(event) => onFieldChange('IsAttorney', event.value)} />

                </div>

                {person.IsAttorney ?
        <ToggleRow
          title="Available for Routing"
          description="Attorneys can be marked available or unavailable without removing the attorney role."
          checked={person.IsAvailable}
          ariaLabel="Available for Routing"
          onChange={(value) => onFieldChange('IsAvailable', value)} /> :

        null}
            </div>

            <div className="people-modal__panel">
                <div className="people-modal__panel-header">
                    <div>
                        <div className="people-modal__panel-title">Billable Rate</div>
                        <div className="people-modal__panel-description">
                            Billable rates are maintained in AttorneyRates in the backend and can apply to attorneys and non-attorneys.
                        </div>
                    </div>
                    <AppToggle
            checked={isBillable}
            ariaLabel="Billable Rate"
            disabled={person.IsAttorney}
            onChange={(event) => onFieldChange('HasBillableRate', event.value)} />

                </div>

                {isBillable ?
        <div className="people-modal__grid">
                        <ModalInputField
            person={person}
            field="HourlyRate"
            label="Hourly Rate"
            value={person.HourlyRate}
            onFieldChange={onFieldChange}
            type="number" />

                    </div> :
        null}
            </div>

            <div className="people-modal__panel">
                <ToggleRow
          title="Administrator"
          description={person.IsCurrentUser && person.IsAdmin
            ? 'Your own administrator access cannot be removed.'
            : 'Allows access to People, Document AI controls, AI oversight, audit data, and spend dashboards.'}
          checked={person.IsAdmin}
          disabled={person.IsCurrentUser && person.IsAdmin}
          ariaLabel="Administrator"
          onChange={(value) => onFieldChange('IsAdmin', value)} />

            </div>

            <div className="people-modal__panel">
                <ToggleRow
          title="Designated Slack Tester"
          description="Enable manual-run Slack redirection for this person."
          checked={person.IsDesignatedSlackTester}
          ariaLabel="Designated Slack Tester"
          onChange={(value) => onFieldChange('IsDesignatedSlackTester', value)} />

            </div>

            <div className="people-modal__panel">
                <div className="people-modal__panel-header">
                    <div>
                        <div className="people-modal__panel-title">Phone</div>
                        <div className="people-modal__panel-description">
                            Assign an extension. Existing extensions are locked to prevent stale PBX records.
                        </div>
                    </div>
                    <PhoneSyncBadge state={person.PhoneSyncState} error={person.PhoneSyncError} />
                </div>

                <div className="people-modal__grid">
                    <ModalInputField
                        person={person}
                        field="Extension"
                        label="Extension"
                        value={person.Extension}
                        onFieldChange={onFieldChange}
                        type="text"
                        disabled={Boolean(person.OriginalExtension)}
                        inputMode="numeric" />
                </div>
            </div>

            <div className="people-modal__panel people-direct-lines">
                <div>
                    <div className="people-modal__panel-title">Direct lines</div>
                    <div className="people-modal__panel-description">
                        Choose one or more numbers. Lines assigned to someone else remain unavailable.
                    </div>
                </div>
                <label className="people-direct-lines__label" htmlFor="people-direct-lines-selector">Direct lines</label>
                <MultiSelect
                    inputId="people-direct-lines-selector"
                    value={person.DirectLines}
                    options={directLineOptions}
                    optionDisabled="disabled"
                    onChange={(event) => onFieldChange('DirectLines', event.value || [])}
                    display="chip"
                    filter
                    showClear
                    maxSelectedLabels={2}
                    selectedItemsLabel="{0} direct lines selected"
                    placeholder={!person.Extension
                      ? 'Assign an extension first'
                      : directLineOptions.length
                        ? 'Select direct lines'
                        : 'No active direct lines'}
                    disabled={!person.Extension || !directLineOptions.length}
                    className="people-direct-lines__select" />
            </div>

            {error ? <div className="people-modal__error">{error}</div> : null}
        </AppModal>);

}



function ToggleRow({ title, description, checked, disabled = false, ariaLabel, onChange }) {
  return (
    <div className="people-modal__toggle-row">
            <div className="people-modal__toggle-copy">
                <div className="people-modal__toggle-title">{title}</div>
                <div className="people-modal__toggle-description">{description}</div>
            </div>
            <AppToggle checked={checked} disabled={disabled} ariaLabel={ariaLabel} onChange={(event) => onChange(event.value)} />
        </div>);

}



function ModalInputField({ person, field, label, value, onFieldChange, type = 'text', disabled = false, inputMode }) {
  const id = inputId(person, field);

  return (
    <div className="people-modal__field">
            <label htmlFor={id}>{label}</label>
            <AppTextInput
        id={id}
        type={type}
        disabled={disabled}
        inputMode={inputMode}
        value={value ?? ''}
        autoComplete="off"
        data-lpignore="true"
        style={{ backgroundColor: 'var(--kc-surface-control)', borderColor: 'var(--kc-surface-edge)' }}
        onChange={(event) => onFieldChange(field, event.target?.value ?? '')} />

        </div>);

}



function buildPersonForm(person = {}) {
  return {
    employeeId: person.employeeId ?? null,
    Name: person.Name ?? '',
    Email: person.Email ?? '',
    ProfilePic: person.ProfilePic ?? null,
    IsActive: person.IsActive !== false,
    IsAdmin: Boolean(person.IsAdmin),
    IsCurrentUser: Boolean(person.IsCurrentUser),
    PipedriveUserID: person.PipedriveUserID ?? '',
    ClockifyUserID: person.ClockifyUserID ?? '',
    TrelloMemberId: person.TrelloMemberId ?? '',
    SlackUserId: person.SlackUserId ?? '',
    IsDesignatedSlackTester: Boolean(person.IsDesignatedSlackTester),
    IsAttorney: Boolean(person.IsAttorney),
    IsAvailable: person.IsAvailable === null || person.IsAvailable === undefined ? true : Boolean(person.IsAvailable),
    HasBillableRate: Boolean(person.HasBillableRate || person.IsAttorney),
    HourlyRate: person.HourlyRate ?? '',
    Extension: person.Extension ?? '',
    OriginalExtension: person.Extension ?? '',
    DirectLines: Array.isArray(person.DirectLines) ? person.DirectLines : [],
    PhoneSyncState: person.PhoneSyncState ?? 'not_configured',
    PhoneSyncError: person.PhoneSyncError ?? null
  };
}



function buildSavePayload(form) {
  return {
    employeeId: form.employeeId,
    Name: String(form.Name || '').trim(),
    Email: String(form.Email || '').trim(),
    IsActive: form.IsActive !== false,
    IsAdmin: Boolean(form.IsAdmin),
    PipedriveUserID: String(form.PipedriveUserID ?? '').trim(),
    ClockifyUserID: String(form.ClockifyUserID ?? '').trim(),
    TrelloMemberId: String(form.TrelloMemberId ?? '').trim(),
    SlackUserId: String(form.SlackUserId ?? '').trim(),
    IsDesignatedSlackTester: Boolean(form.IsDesignatedSlackTester),
    IsAttorney: Boolean(form.IsAttorney),
    IsAvailable: Boolean(form.IsAvailable),
    HasBillableRate: Boolean(form.IsAttorney || form.HasBillableRate),
    HourlyRate: String(form.HourlyRate ?? '').trim(),
    Extension: String(form.Extension ?? '').trim(),
    DirectLines: Array.isArray(form.DirectLines) ? form.DirectLines : []
  };
}



function validatePersonForm(form) {
  if (!String(form.Name || '').trim()) {
    return 'Name is required.';
  }

  if (form.IsAttorney || form.HasBillableRate) {
    const hourlyRate = String(form.HourlyRate ?? '').trim();
    if (!/^\d+$/.test(hourlyRate)) {
      return 'A whole-number hourly rate is required when attorney or billable rate is enabled.';
    }
  }

  const extension = String(form.Extension || '').trim();
  if (extension && !/^\d{3}$/.test(extension)) {
    return 'Extension must be a three-digit number.';
  }
  if (form.DirectLines?.length && !extension) {
    return 'Assign an extension before selecting a direct line.';
  }

  return '';
}



function matchesFilters(person, filters) {
  const search = filters.search.trim().toLowerCase();
  if (search) {
    const searchableValues = [
    person.Name,
    person.Email,
    person.SlackUserId,
    person.PipedriveUserID,
    person.ClockifyUserID,
    person.TrelloMemberId]
    .concat([person.Extension, ...(person.DirectLines || [])])

    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

    if (!searchableValues.some((value) => value.includes(search))) {
      return false;
    }
  }

  if (!matchesArrayFilter(filters.status, person.IsActive === false ? 'departed' : 'active')) {
    return false;
  }

  if (!matchesArrayFilter(filters.role, person.IsAttorney ? 'attorney' : 'non_attorney')) {
    return false;
  }

  if (!matchesArrayFilter(filters.billable, person.HasBillableRate ? 'has_rate' : 'no_rate')) {
    return false;
  }

  if (!matchesArrayFilter(filters.tester, person.IsDesignatedSlackTester ? 'tester' : 'standard')) {
    return false;
  }

  return true;
}



function matchesArrayFilter(selectedValues, currentValue) {
  return selectedValues.length === 0 || selectedValues.includes(currentValue);
}



function getDefaultFilterValue(field) {
  if (field === 'search') {
    return '';
  }

  const defaults = createDefaultFilters();
  return Array.isArray(defaults[field]) ? [...defaults[field]] : defaults[field];
}



function isDefaultFilterValue(field, value) {
  const defaultValue = getDefaultFilterValue(field);
  if (Array.isArray(defaultValue)) {
    return arrayValuesEqual(defaultValue, value);
  }
  return defaultValue === value;
}



function arrayValuesEqual(left, right) {
  const leftValues = Array.isArray(left) ? [...left].sort() : [];
  const rightValues = Array.isArray(right) ? [...right].sort() : [];
  if (leftValues.length !== rightValues.length) {
    return false;
  }
  return leftValues.every((value, index) => value === rightValues[index]);
}



function toggleFilterValue(values, nextValue) {
  return values.includes(nextValue) ?
  values.filter((value) => value !== nextValue) :
  [...values, nextValue];
}



function inputId(person, field) {
  const rawId = person?.employeeId === undefined || person?.employeeId === null ? 'new' : String(person.employeeId);
  return `person-${rawId.replace(/[^a-zA-Z0-9_-]/g, '-')}-${field}`;
}



function detailValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'Not set';
  }
  return String(value);
}



function formatRate(value) {
  return `$${value}/hr`;
}

export default People;
